import { Toast } from '@revenuemarket/ui';
import { downloadFromBlob } from '@revenuemarket/utils/downloadFromBlob';
import dayjs, { Dayjs } from 'dayjs';

import {
  DownloadDataType,
  DownloadFileByPostRequest,
  downloadFileByPost,
  getExportFile,
} from '~apis/download';
import { DatePickerRangeType } from '~components/ui/DatePicker/DateRangePickerWithPreset';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { mixpanel } from '~pages/_app';

export type ExcelExportFormat = 'BASIC' | 'WEHAGO';

export interface ExportRequest {
  companyId: string;
  startDate: Dayjs;
  endDate: Dayjs;
  rangeType?: 'DATE' | 'WEEK' | 'MONTH' | 'QUARTER' | 'YEAR';
  dataType: DownloadDataType;
  fileType: 'EXCEL' | 'ZIP';
  billingItemRangeType?: 'PAYMENT' | 'USED_DATE' | 'TRANSACTION_DATE';
  fxRuleType?: 'TRANSACTION_DATE' | 'END_OF_PERIOD';
  exportFormat?: 'BASIC' | 'WEHAGO';
}

export const exportFile = async ({
  companyId,
  startDate,
  endDate,
  dataType,
  fileType,
  rangeType,
  fxRuleType,
  billingItemRangeType,
  exportFormat,
}: ExportRequest) => {
  mixpanel.track(MIXPANEL_EVENT.DOWNLOAD.DEFAULT, {
    type: 'excel',
    dataType: dataType,
    fileType: fileType,
    rangeType: rangeType,
  });
  const response = await getExportFile({
    companyId,
    startDate,
    endDate,
    rangeType,
    dataType,
    fileType,
    fxRuleType,
    billingItemRangeType,
    exportFormat,
  });
  if (response.status === 200) {
    downloadFromBlob(
      response.data,
      getDefaultFileName(dataType, startDate, endDate, fileType),
      response.headers,
    );
    Toast.success('다운로드가 완료되었습니다.');
  } else {
    Toast.error('다운로드에 실패했습니다.');
  }
};

export const exportExcelFileByPost = async ({
  companyId,
  startDate,
  endDate,
  dataType,
  fileType,
  rangeType,
  ...params
}: DownloadFileByPostRequest) => {
  mixpanel.track(MIXPANEL_EVENT.DOWNLOAD.DEFAULT, {
    type: 'excel',
    dataType: dataType,
    fileType: fileType,
    rangeType: rangeType,
  });
  const response = await downloadFileByPost({
    companyId,
    startDate,
    endDate,
    rangeType,
    dataType,
    fileType,
    ...params,
  });
  if (response.status === 200) {
    downloadFromBlob(
      response.data,
      getDefaultFileName(dataType, startDate, endDate, fileType),
      response.headers,
    );
    Toast.success('다운로드가 완료되었습니다.');
  } else {
    Toast.error('다운로드에 실패했습니다.');
  }
};

const getDefaultFileName = (
  dataType: DownloadDataType,
  startDate: Dayjs,
  endDate: Dayjs,
  fileType: 'EXCEL' | 'ZIP',
) => {
  const ext = fileType === 'EXCEL' ? 'xlsx' : 'zip';
  const today = dayjs().format('YYYY-MM-DD');
  switch (dataType) {
    case 'BANK_ACCOUNTS':
      return `계좌_${today}.${ext}`;
    case 'CASH_FLOW':
      return `손익_${today}.${ext}`;
    case 'SALES_INVOICE_MAPPING':
      return `매출 정산_${today}.${ext}`;
    case 'PURCHASE_INVOICE_MAPPING':
      return `매입 정산_${today}.${ext}`;
    case 'BANK_TRANSACTION_LABELS':
      return `은행 거래내역 분류_${today}.${ext}`;
    case 'CARD_BILLING_ITEM_LABELS':
    case 'UNIFIED_CARD_TRANSACTION_LABELS':
      return `카드 이용내역 분류_${today}.${ext}`;
    case 'BANK_TRANSACTION_DATA':
      return `은행 거래내역_${today}.${ext}`;
    case 'CARD_APPROVAL_TRANSACTION_DATA':
      return `카드승인_${today}.${ext}`;
    case 'CARD_PURCHASE_TRANSACTION_DATA':
      return `카드매입_${today}.${ext}`;
    case 'BANK_TRANSACTION_DATA_WEHAGO':
      return `은행 거래내역(WEHAGO)_${today}.${ext}`;
    case 'CARD_PURCHASE_TRANSACTION_DATA_WEHAGO':
      return `카드매입(WEHAGO)_${today}.${ext}`;
    case 'TAX_INVOICE_DATA':
      return `세금계산서_${today}.${ext}`;
    case 'CASH_RECEIPT_DATA':
      return `현금영수증_${today}.${ext}`;
  }
};

export const convertRangeType = (
  rangeType: DatePickerRangeType,
): 'DATE' | 'WEEK' | 'MONTH' | 'QUARTER' | 'YEAR' => {
  if (rangeType === 'date') {
    return 'DATE';
  }
  if (rangeType === 'week') {
    return 'WEEK';
  }
  if (rangeType === 'month') {
    return 'MONTH';
  }
  if (rangeType === 'quarter') {
    return 'QUARTER';
  }
  if (rangeType === 'year') {
    return 'YEAR';
  }
  return 'DATE';
};
